import React from "react"
import classNames from "classnames"
import { object, string } from "yup"
import { withFormik } from "formik"

import Input from "../../components/Input/Input"
import Dropdown from "../../components/Dropdown/Dropdown"
import styles from "./CompanyEmployeeForm.module.scss"
import Button from "../../components/Button/Button"
import { isHaveBadWords } from "../../helpers/badWords"

const employeeRoleOptions = [
  { id: "user", value: "User" },
  { id: "admin", value: "Admin" },
]

const CompanyEmployeeForm = ({
  data = {},
  values,
  touched,
  errors,
  handleChange,
  handleBlur,
  handleSubmit,
  closeAction,
  setFieldValue,
}) => {
  const isNew = Object.keys(data).length === 0

  const handleRoleChange = (nameOrOption, option) => {
    const selected = option || nameOrOption
    setFieldValue("employeeRole", selected.id)
  }

  return (
    <form className={styles.formWrapper} onSubmit={handleSubmit}>
      <div className={styles.fieldsWrapper}>
        <Input
          value={values.fullName}
          label="Full name"
          name="fullName"
          placeholder="Enter full name"
          fieldStyle="light"
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.fullName && touched.fullName}
          errorMessage={errors.fullName && touched.fullName && errors.fullName}
        />

        <Input
          value={values.email}
          label="Email"
          name="email"
          placeholder="Enter email"
          fieldStyle="light"
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.email && touched.email}
          errorMessage={errors.email && touched.email && errors.email}
        />

        <Dropdown
          id="employeeRole"
          label="Employee role*"
          placeholder="Employee Role"
          fieldStyle="light"
          options={employeeRoleOptions}
          value={
            values.employeeRole
              ? employeeRoleOptions.find((opt) => opt.id === values.employeeRole) || {}
              : {}
          }
          onChange={handleRoleChange}
          error={errors.employeeRole && touched.employeeRole}
          errorMessage={
            errors.employeeRole && touched.employeeRole && errors.employeeRole
          }
        />
      </div>

      <div
        className={classNames(styles.btsWrapper, {
          [styles.oneButtonWrapper]: isNew,
        })}
      >
        {!isNew && (
          <Button buttonStyle="outlinedBlack" onClick={closeAction}>
            Cancel
          </Button>
        )}

        <Button
          buttonStyle="black"
          onClick={handleSubmit}
          disabled={
            !values.fullName.trim() ||
            !values.email.trim() ||
            !values.employeeRole ||
            Object.values(errors).length > 0
          }
        >
          {isNew ? "Invite" : "Save"}
        </Button>
      </div>
    </form>
  )
}

export default withFormik({
  mapPropsToValues: ({ data }) => {
    if (!!data) {
      return {
        fullName: data.full_name,
        email: data.email,
        employeeRole: data.employee_role || "user",
      }
    }
    return { fullName: "", email: "", employeeRole: "user" }
  },
  validationSchema: () => {
    return object().shape({
      fullName: string()
        .trim()
        .matches(
          /^[a-zA-Z\s_\-_'_"]*$/,
          "Enter a correct Full Name that doesn't contain any special characters or numbers",
        )
        .min(
          2,
          "Please enter a correct full name that consists of a minimum 2 characters",
        )
        .max(
          32,
          "Please enter a correct full name that consists of no more than 32 characters",
        )
        .test({
          name: "description_obj",
          test: (val) => {
            if (!val) return true
            if (val) {
              return !isHaveBadWords(val)
            }
          },
          message: `Please don't use bad language`,
        })
        .required("Please fill the name"),
      email: string()
        .email("Please enter your email address in format yourname@example.com")
        .max(
          64,
          "Please enter a valid email address that consists of no more than 64 characters",
        )
        .test({
          name: "description_obj",
          test: (val) => {
            if (!val) return true
            if (val) {
              return !isHaveBadWords(val)
            }
          },
          message: `Please don't use bad language`,
        })
        .required("Please enter email address"),
      employeeRole: string().required("Please select employee role"),
    })
  },
  handleSubmit: (
    values,
    {
      setSubmitting,
      setFieldError,
      props: { onSubmit, closeAction, setVisiblePopup },
    },
  ) => {
    onSubmit({ ...values, employee_role: values.employeeRole })
      .then(() => {
        closeAction()
        setVisiblePopup("invited")
      })
      .catch((e) => {
        const errorMsg =
          e?.response?.data?.error || e?.message || "Something went wrong"
        setFieldError("email", errorMsg)
      })
      .finally(() => setSubmitting(false))
  },
})(CompanyEmployeeForm)
